import React, { PureComponent } from "react";
import _ from "lodash";
import autoBind from "react-autobind";
import { Button, FormControl } from "react-bootstrap";
import { Controlled as CodeMirror } from "react-codemirror2";

import ChecklistPreviewInline from "../app-checklist-preview-inline";
import { previewChecklistInline, setPreviewActivity } from "../../actions/actions";
import { reloadActivitiesFast } from "../../actions/playerActions";
import { codeMirrorOptionsJson } from "../../constants/appConstants";

require("codemirror/lib/codemirror.css"); // eslint-disable-line
require("codemirror/theme/material.css"); // eslint-disable-line
require("codemirror/mode/javascript/javascript"); // eslint-disable-line
require("codemirror/mode/htmlmixed/htmlmixed"); // eslint-disable-line
require("codemirror/addon/fold/foldcode"); // eslint-disable-line
require("codemirror/addon/fold/foldgutter"); // eslint-disable-line
require("codemirror/addon/fold/brace-fold.js"); // eslint-disable-line
require("codemirror/addon/fold/foldgutter.css"); // eslint-disable-line
require("codemirror/addon/lint/lint"); // eslint-disable-line
require("codemirror/addon/lint/json-lint"); // eslint-disable-line
require("codemirror/addon/lint/lint.css"); // eslint-disable-line

const CURRENT_DRAFT_TARGET = "__current_draft__";

function stringifyContent(content, emptyValue) {
	let nextContent = content;
	if (!nextContent || _.isEmpty(nextContent)) {
		nextContent = emptyValue;
	}
	if (!_.isString(nextContent)) {
		nextContent = JSON.stringify(nextContent, null, 4);
	}
	return nextContent;
}

export default class JsonPreviewEditor extends PureComponent {
	constructor(props) {
		super(props);
		autoBind(this);

		const editContent = stringifyContent(props.content, props.emptyValue);
		const initialState = {
			editContent,
			lastValidJson: null,
			validity: { valid: true, error: null },
			selectedPreviewTargetId: "",
			instanceCount: 0,
			loadingPreview: false,
			loadingActivities: false,
			attemptedActivityLoad: false
		};

		try {
			initialState.lastValidJson = JSON.parse(editContent);
		} catch (e) {
			console.warn(e);
		}

		this.lastEdit = editContent;
		this.editorRef = React.createRef();
		this.state = initialState;

		if (_.isFunction(props.onChange)) {
			props.onChange(editContent);
		}
	}

	componentDidMount() {
		this.ensurePreviewTargetReady();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.content !== this.props.content) {
			const nextContent = stringifyContent(this.props.content, this.props.emptyValue);
			if (nextContent !== this.state.editContent) {
				const nextState = {
					editContent: nextContent
				};
				try {
					nextState.lastValidJson = JSON.parse(nextContent);
					nextState.validity = { valid: true, error: null };
				} catch (e) {
					console.warn(e);
				}
				this.lastEdit = nextContent;
				this.setState(nextState);
			}
		}

		if (
			prevProps.activities !== this.props.activities ||
			prevProps.activitiesMap !== this.props.activitiesMap ||
			prevProps.selectedChecklistId !== this.props.selectedChecklistId ||
			prevProps.root !== this.props.root
		) {
			this.ensurePreviewTargetReady();
		}
	}

	getPreviewTargets(props = this.props) {
		const targetsById = {};
		const targets = [];
		const root = _.isPlainObject(props.root) ? props.root : {};
		const rootName = root.name || root.title || root.id;

		if (root.id && rootName) {
			targetsById[CURRENT_DRAFT_TARGET] = {
				value: CURRENT_DRAFT_TARGET,
				label: `[Current Draft] ${rootName}`,
				type: "current"
			};
			targets.push(targetsById[CURRENT_DRAFT_TARGET]);
		}

		const pushActivity = (activity) => {
			if (!activity || !activity.id || targetsById[activity.id]) {
				return;
			}
			targetsById[activity.id] = {
				value: activity.id,
				label: activity.name || activity.title || activity.id,
				type: "saved"
			};
			targets.push(targetsById[activity.id]);
		};

		(props.activities || []).forEach(pushActivity);

		const selectedActivity = _.get(props, ["activitiesMap", props.selectedChecklistId], null);
		pushActivity(selectedActivity);

		Object.values(props.activitiesMap || {}).forEach(pushActivity);

		return targets;
	}

	getPreferredPreviewTargetId(targets = this.getPreviewTargets()) {
		if (!targets.length) {
			return "";
		}
		if (_.find(targets, { value: CURRENT_DRAFT_TARGET })) {
			return CURRENT_DRAFT_TARGET;
		}
		if (this.props.selectedChecklistId && _.find(targets, { value: this.props.selectedChecklistId })) {
			return this.props.selectedChecklistId;
		}
		return targets[0].value;
	}

	ensurePreviewTargetReady() {
		const targets = this.getPreviewTargets();
		if (!targets.length) {
			this.loadActivitiesIfNeeded();
			return;
		}

		if (!this.state.selectedPreviewTargetId) {
			this.loadPreviewTarget(this.getPreferredPreviewTargetId(targets));
		}
	}

	loadActivitiesIfNeeded() {
		if (this.state.attemptedActivityLoad || this.state.loadingActivities) {
			return;
		}
		this.setState({
			loadingActivities: true,
			attemptedActivityLoad: true
		});
		reloadActivitiesFast("").catch((err) => {
			console.error(err);
		}).finally(() => {
			this.setState({
				loadingActivities: false
			});
		});
	}

	handleChangeEditContent(editContent) {
		let lastValidJson = null;
		try {
			lastValidJson = JSON.parse(editContent);
		} catch (e) {} // eslint-disable-line

		const nextState = {
			editContent
		};
		if (lastValidJson) {
			nextState.lastValidJson = lastValidJson;
			nextState.validity = { valid: true, error: null };
		}

		this.setState(nextState);
		if (_.isFunction(this.props.onChange)) {
			this.props.onChange(editContent);
		}
	}

	handleChangeValidity(valid, error) {
		const validity = { valid, error };
		this.setState({ validity });
		if (_.isFunction(this.props.onValidityChange)) {
			this.props.onValidityChange(validity);
		}
	}

	loadPreviewTarget(targetId) {
		if (!targetId) {
			return;
		}
		this.setState({
			loadingPreview: true
		});

		const finish = () => {
			this.setState((prevState) => ({
				selectedPreviewTargetId: targetId,
				instanceCount: prevState.instanceCount + 1,
				loadingPreview: false
			}));
		};

		const promise = targetId === CURRENT_DRAFT_TARGET
			? previewChecklistInline()
			: setPreviewActivity(targetId);

		promise.then(() => {
			setTimeout(finish, 200);
		}).catch((err) => {
			console.error(err);
			this.setState({
				loadingPreview: false
			});
		});
	}

	handleSelectActivityForPreview(e) {
		this.loadPreviewTarget(e.target.value);
	}

	handleRefreshPreview() {
		if (!this.state.lastValidJson || !this.state.selectedPreviewTargetId || this.state.loadingPreview) {
			return;
		}
		if (this.state.selectedPreviewTargetId === CURRENT_DRAFT_TARGET) {
			this.loadPreviewTarget(CURRENT_DRAFT_TARGET);
			return;
		}
		this.setState((prevState) => ({
			instanceCount: prevState.instanceCount + 1
		}));
	}

	renderToolbar() {
		const targets = this.getPreviewTargets();
		const noTargets = targets.length === 0;
		return (
			<div style={{ display: "flex", gap: "12px", alignItems: "center", padding: "10px 12px", borderBottom: "1px solid #e5e5e5", flexWrap: "wrap" }}>
				<div style={{ fontWeight: 600 }}>{this.props.previewLabel || "Preview Activity"}</div>
				<FormControl
					componentClass="select"
					value={this.state.selectedPreviewTargetId}
					onChange={this.handleSelectActivityForPreview}
					disabled={this.state.loadingPreview || this.state.loadingActivities || noTargets}
					style={{ width: "320px", maxWidth: "100%" }}
				>
					<option value="">{this.state.loadingActivities ? "Loading activities..." : "Select an activity..."}</option>
					{targets.map((target) => (
						<option key={target.value} value={target.value}>{target.label}</option>
					))}
				</FormControl>
				<Button
					onClick={this.handleRefreshPreview}
					disabled={!this.state.lastValidJson || !this.state.selectedPreviewTargetId || this.state.loadingPreview}
				>
					Refresh Preview
				</Button>
				{this.state.loadingPreview && <span style={{ color: "#666" }}>Loading preview...</span>}
				{!this.state.validity.valid && <span style={{ color: "#a94442" }}>Preview is using the last valid JSON.</span>}
				{!this.state.loadingActivities && noTargets && <span style={{ color: "#666" }}>No activities available to preview against.</span>}
			</div>
		);
	}

	renderEditor() {
		return (
			<div style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", overflow: "hidden", borderRight: "1px solid #e5e5e5" }}>
				<CodeMirror
					value={this.state.editContent}
					ref={this.editorRef}
					options={codeMirrorOptionsJson}
					onBeforeChange={(editor, data, value) => {
						this.lastEdit = value;
						this.handleChangeEditContent(value);
					}}
					editorDidMount={(editor) => {
						this.editor = editor;
						setTimeout(() => {
							editor.refresh();
						}, 200);
					}}
					className="react-codemirror2 stretch"
					onBlur={() => {
						try {
							if (!_.isEmpty(this.lastEdit)) {
								const lastValidJson = JSON.parse(this.lastEdit);
								this.setState({ lastValidJson });
								this.handleChangeValidity(true);
							}
						} catch (e) {
							this.handleChangeValidity(false, e);
							alert(`${e.message}\n\nThe edited content is not valid JSON, please make sure to fix all issues before attempting to save.`);
						}
					}}
				/>
			</div>
		);
	}

	renderPreview() {
		const preferenceObject = this.state.lastValidJson ? { [this.props.previewPreferenceKey]: this.state.lastValidJson } : {};
		return (
			<div style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
				{this.renderToolbar()}
				<div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "12px" }}>
					{this.state.selectedPreviewTargetId && this.state.lastValidJson && !this.state.loadingPreview && (
						<ChecklistPreviewInline
							key={`preview-${this.state.selectedPreviewTargetId}-${this.state.instanceCount}`}
							cacheKey={this.state.instanceCount}
							disableVoice
							orgPreferences={preferenceObject}
						/>
					)}
					{!this.state.selectedPreviewTargetId && !this.state.loadingActivities && (
						<div style={{ color: "#666" }}>Select an activity to preview {this.props.previewNoun || "this JSON"}.</div>
					)}
				</div>
			</div>
		);
	}

	render() {
		return (
			<div style={{ minHeight: 0, height: "100%", display: "flex", flex: 1, overflow: "hidden" }}>
				{this.renderEditor()}
				{this.renderPreview()}
			</div>
		);
	}
}
